import * as React from 'react';
import { useState } from 'react';

import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TextCellProps {
  children: React.ReactNode;
  className?: string;
  /**
   * When true and `truncate` is set, measures overflow on hover (`scrollWidth > clientWidth`)
   * and shows a native `title` tooltip only if the text is actually cropped. The title is
   * cleared as soon as the pointer leaves.
   */
  showTitleOnOverflow?: boolean;
  truncate?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Standard muted-text cell for data table columns.
 * Replaces the repeated `<span className="text-sm text-muted-foreground">` pattern.
 */
export function TextCell({
  children,
  className,
  showTitleOnOverflow = false,
  truncate = true,
}: TextCellProps): React.JSX.Element {
  const showsTitleOnOverflow = showTitleOnOverflow && truncate;
  const [overflowTitle, setOverflowTitle] = useState<string>();

  function handleMouseEnter(event: React.MouseEvent<HTMLSpanElement>): void {
    const el = event.currentTarget;
    setOverflowTitle(el.scrollWidth > el.clientWidth ? (el.textContent ?? undefined) : undefined);
  }

  function handleMouseLeave(): void {
    setOverflowTitle(undefined);
  }

  return (
    <span
      className={cn(
        'text-sm text-muted-foreground',
        truncate && 'truncate',
        // `scrollWidth`/`clientWidth` are always 0 on a `display: inline` element in browsers,
        // so the overflow measurement in `handleMouseEnter` needs a block-level box to work.
        showsTitleOnOverflow && 'block',
        className,
      )}
      onMouseEnter={showsTitleOnOverflow ? handleMouseEnter : undefined}
      onMouseLeave={showsTitleOnOverflow ? handleMouseLeave : undefined}
      title={showsTitleOnOverflow ? overflowTitle : undefined}
    >
      {children}
    </span>
  );
}
